import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ToastContainer, toast } from "react-toastify";
import dayjs from "dayjs";
import FinalNightSetupPanel from "../../component/admin/competitions/FinalNightSetupPanel";
import AdminRoundLeaderboard from "../../component/admin/competitions/AdminRoundLeaderboard";
import {
  canEnableRound,
  getRoundLockReason,
} from "../../utils/finalNightRoundControl";
import {
  getAdminFinalNight,
  startFinalNight,
  updateFinalNightSettings,
  endFinalNight,
  enableFinalNightRound,
  completeFinalNightRound,
  eliminateContestants,
  advanceToNextRound,
} from "../../api/competitionFinalNight";

const gold = "#C9A66B";

export default function CompetitionFinalNight() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [startAt, setStartAt] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
  const [endAt, setEndAt] = useState(
    dayjs().add(1, "day").format("YYYY-MM-DDTHH:mm")
  );
  const [selectedElim, setSelectedElim] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedRoundIds, setExpandedRoundIds] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getAdminFinalNight(id, token);
      if (res.data.success) {
        setData(res.data.data);
        const c = res.data.data.competition;
        setYoutubeUrl(c.final_night_youtube_url || "");
        if (c.final_night_start) {
          setStartAt(dayjs(c.final_night_start).format("YYYY-MM-DDTHH:mm"));
        }
        if (c.final_night_end) {
          setEndAt(dayjs(c.final_night_end).format("YYYY-MM-DDTHH:mm"));
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    load();
  }, [load]);

  const competition = data?.competition;
  const rounds = competition?.rounds || [];
  const activeRound = data?.activeRound;
  const leaderboard = data?.leaderboard || [];
  const roundLeaderboards = data?.roundLeaderboards || {};
  const fnStatus = competition?.final_night_status || "off";
  const isStopped = competition?.status === "stopped";
  const finalNightStarted = fnStatus === "live" || fnStatus === "ended";

  useEffect(() => {
    if (activeRound?.id) {
      setExpandedRoundIds(new Set([activeRound.id]));
    }
  }, [activeRound?.id]);

  const stepBadge = (n, active) => (
    <Chip
      size="small"
      label={`Step ${n}`}
      sx={{
        mr: 1,
        fontWeight: 800,
        bgcolor: active ? gold : "#f3f4f6",
        color: active ? "#1a1d27" : "#6b7280",
      }}
    />
  );

  const handleStart = async () => {
    try {
      const res = await startFinalNight(
        {
          id: Number(id),
          final_night_start: startAt,
          final_night_end: endAt,
          final_night_youtube_url: youtubeUrl,
        },
        token
      );
      if (res.data.success) {
        toast.success(res.data.message);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not start Final Night");
    }
  };

  const handleSaveLink = async () => {
    try {
      await updateFinalNightSettings(
        {
          id: Number(id),
          final_night_youtube_url: youtubeUrl,
          ...(finalNightStarted
            ? { final_night_start: startAt, final_night_end: endAt }
            : {}),
        },
        token
      );
      toast.success(finalNightStarted ? "Live settings saved" : "Live stream link saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    }
  };

  const getRoundLeaderboard = (round) => {
    if (!round) return [];
    if (roundLeaderboards[round.id]?.length) return roundLeaderboards[round.id];
    if (activeRound?.id === round.id) return leaderboard;
    return [];
  };

  const toggleRoundLeaderboard = (roundId) => {
    setExpandedRoundIds((prev) => {
      const next = new Set(prev);
      if (next.has(roundId)) next.delete(roundId);
      else next.add(roundId);
      return next;
    });
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      const { type, round, nextRound } = confirmAction;
      if (type === "enable") {
        const res = await enableFinalNightRound(
          { competition_id: Number(id), round_id: round.id },
          token
        );
        toast.success(res.data.message);
        setSelectedElim([]);
      } else if (type === "stop") {
        const res = await completeFinalNightRound({ round_id: round.id }, token);
        toast.success(res.data.message);
        setSelectedElim([]);
      } else if (type === "advance") {
        const res = await advanceToNextRound(
          { competition_id: Number(id), next_round_id: nextRound.id },
          token
        );
        toast.success(res.data.message);
        setSelectedElim([]);
      } else if (type === "end") {
        await endFinalNight({ id: Number(id) }, token);
        toast.success("Final Night ended");
      }
      setConfirmAction(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEliminate = async () => {
    if (!activeRound || selectedElim.length === 0) return;
    try {
      await eliminateContestants(
        { round_id: activeRound.id, contestant_ids: selectedElim },
        token
      );
      toast.success("Contestants eliminated");
      setSelectedElim([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Elimination failed");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: gold }} />
      </Box>
    );
  }

  return (
    <>
      <ToastContainer />
      <Box className="admin-competition-final-night">
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate("/admin/dashboard/competitions")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Final Night — Live Control
            </Typography>
            <Typography color="text.secondary" fontSize={14}>
              {competition?.name} · operational controls only
            </Typography>
          </Box>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            label={
              fnStatus === "live"
                ? "LIVE"
                : fnStatus === "ended"
                  ? "ENDED"
                  : "NOT STARTED"
            }
            color={fnStatus === "live" ? "success" : "default"}
            sx={{ ml: "auto", fontWeight: 700 }}
          />
        </Stack>

        <Paper
          elevation={0}
          sx={{ p: 2.5, mb: 2, borderRadius: 3, border: "1px solid #e8dcc4" }}
        >
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            {stepBadge(1, true)}
            <Typography fontWeight={800}>Scoring setup</Typography>
          </Stack>
          <FinalNightSetupPanel
            competitionId={id}
            token={token}
            judgesPercent={competition?.judges_percent}
            usersPercent={competition?.users_percent}
            apiRounds={rounds}
            hasActiveRound={Boolean(activeRound)}
            competitionStopped={isStopped}
            finalNightStarted={finalNightStarted}
            onSaved={load}
          />
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: "1px solid #eceef3" }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            {stepBadge(2, fnStatus === "live")}
            <Typography fontWeight={800} sx={{ color: "#9a7b3f" }}>
              Go live — YouTube stream
            </Typography>
          </Stack>
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 1.5 }}>
            Start Final Night when the show begins. You can change stream link,
            dates, and scoring setup at any time while live.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="YouTube live link"
              fullWidth
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/live/VIDEO_ID or watch?v=VIDEO_ID"
              helperText="Use the watch or /live/VIDEO_ID link from YouTube Studio (not @channel/live). Required for Safari embed."
            />
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                label="Final Night start"
                type="datetime-local"
                fullWidth
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Final Night end (1 day)"
                type="datetime-local"
                fullWidth
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {fnStatus === "off" && competition?.status === "stopped" ? (
                <Button
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={handleStart}
                  disabled={rounds.length === 0}
                  sx={{ bgcolor: gold, "&:hover": { bgcolor: "#b8925a" } }}
                >
                  Start Final Night
                </Button>
              ) : null}
              {fnStatus === "live" ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopCircleOutlinedIcon />}
                  onClick={() => setConfirmAction({ type: "end" })}
                >
                  End Final Night
                </Button>
              ) : null}
              <Button variant="outlined" onClick={handleSaveLink}>
                {finalNightStarted ? "Save live settings" : "Save live link"}
              </Button>
            </Stack>
            {competition?.status !== "stopped" && fnStatus === "off" ? (
              <Typography fontSize={13} color="warning.main">
                Competition must be stopped before starting Final Night.
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: "1px solid #eceef3" }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            {stepBadge(3, Boolean(activeRound))}
            <Typography fontWeight={800}>Round control</Typography>
          </Stack>
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 2 }}>
            Enable one round at a time during the live show. Once you advance to
            the next round, previous rounds cannot be re-enabled. Round 1 starts
            with event votes from the competition; user votes reset when you
            advance.
          </Typography>
          {fnStatus !== "live" ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Start Final Night above to enable round controls.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              Final Night is live — enable rounds in order. You will be asked to
              confirm before activating, stopping, or advancing any round.
            </Alert>
          )}
          <Stack spacing={1.5}>
            {rounds.map((r) => {
              const isActive = Boolean(r.is_enabled);
              const nextRound = rounds.find(
                (rr) => rr.round_order === r.round_order + 1
              );
              const enableAllowed = canEnableRound(r, rounds, fnStatus);
              const lockReason = getRoundLockReason(r, rounds, fnStatus);
              const roundLb = getRoundLeaderboard(r);
              const isCompleted = r.round_status === "completed";
              const lbOpen = expandedRoundIds.has(r.id);
              return (
                <Box
                  key={r.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: isActive ? `2px solid ${gold}` : "1px solid #eceef3",
                    bgcolor: isActive ? "#fffbf5" : "#fafbfc",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={700}>
                        Round {r.round_order}: {r.name}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {r.judges?.length || 0} judge(s) · {r.round_status}
                        {isCompleted ? " · locked" : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {!isActive && fnStatus === "live" ? (
                        <Tooltip title={lockReason || ""} disableHoverListener={enableAllowed}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!enableAllowed}
                              onClick={() =>
                                setConfirmAction({ type: "enable", round: r })
                              }
                              sx={{ bgcolor: gold, "&:hover": { bgcolor: "#b8925a" } }}
                            >
                              Enable
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {isActive && fnStatus === "live" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setConfirmAction({ type: "stop", round: r })}
                        >
                          Stop round
                        </Button>
                      ) : null}
                      {isActive && nextRound && fnStatus === "live" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setConfirmAction({
                              type: "advance",
                              round: r,
                              nextRound,
                            })
                          }
                        >
                          Complete & next round
                        </Button>
                      ) : null}
                      {isActive ? (
                        <Chip size="small" label="ACTIVE" color="success" />
                      ) : isCompleted ? (
                        <Chip size="small" label="COMPLETED" />
                      ) : null}
                    </Stack>
                  </Stack>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => toggleRoundLeaderboard(r.id)}
                    endIcon={lbOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: gold, fontWeight: 700, px: 0, mb: lbOpen ? 1 : 0 }}
                  >
                    {lbOpen ? "Hide leaderboard" : "View leaderboard"}
                  </Button>
                  <Collapse in={lbOpen}>
                    <AdminRoundLeaderboard
                      rows={roundLb}
                      compact
                      emptyMessage={
                        isActive
                          ? "Scores will appear as judges score and users vote."
                          : "No scores recorded for this round yet."
                      }
                    />
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {fnStatus === "live" ? (
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: "1px solid #eceef3" }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
              {stepBadge(4, true)}
              <Typography fontWeight={800}>Live scoring</Typography>
            </Stack>
            <Typography color="text.secondary" fontSize={14}>
              Pre-round vote adjustments are on the competition list (vote icon)
              or Edit Competition. Use elimination and leaderboard below for Final
              Night live control.
            </Typography>
          </Paper>
        ) : null}

        {activeRound && fnStatus === "live" ? (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #eceef3" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              sx={{ mb: 2 }}
            >
              <Typography fontWeight={700}>
                Leaderboard — {activeRound.name}
              </Typography>
              <Button
                variant="contained"
                color="error"
                disabled={selectedElim.length === 0}
                onClick={handleEliminate}
              >
                Eliminate selected ({selectedElim.length})
              </Button>
            </Stack>
            <Stack spacing={1}>
              {(() => {
                const sortedLb = [...getRoundLeaderboard(activeRound)].sort((a, b) => {
                  const aElim = Boolean(a.contestant?.is_eliminated);
                  const bElim = Boolean(b.contestant?.is_eliminated);
                  if (aElim !== bElim) return aElim ? 1 : -1;
                  return Number(b.total_score) - Number(a.total_score);
                });
                const activeRankByRowId = new Map();
                sortedLb
                  .filter((r) => !r.contestant?.is_eliminated)
                  .forEach((r, i) => activeRankByRowId.set(r.id, i + 1));
                return sortedLb.map((row) => {
                  const c = row.contestant;
                  if (!c) return null;
                  const eliminated = Boolean(c.is_eliminated);
                  const activeRank = activeRankByRowId.get(row.id) || null;
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: eliminated
                          ? "#f3f4f6"
                          : activeRank && activeRank <= 3
                            ? "#fffbf5"
                            : "#fafbfc",
                        border: "1px solid #eceef3",
                        opacity: eliminated ? 0.72 : 1,
                        filter: eliminated ? "grayscale(0.75)" : "none",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedElim.includes(c.id)}
                            disabled={eliminated}
                            onChange={(e) => {
                              setSelectedElim((prev) =>
                                e.target.checked
                                  ? [...prev, c.id]
                                  : prev.filter((x) => x !== c.id)
                              );
                            }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      <Typography
                        fontWeight={800}
                        sx={{
                          width: 28,
                          color: eliminated ? "#9ca3af" : gold,
                        }}
                      >
                        {eliminated ? "—" : `#${activeRank}`}
                      </Typography>
                      <Avatar
                        src={c.profile_image}
                        sx={{ width: 40, height: 40, opacity: eliminated ? 0.65 : 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: eliminated ? "#6b7280" : "inherit",
                              textDecoration: eliminated ? "line-through" : "none",
                            }}
                          >
                            {c.full_name}
                          </Typography>
                          {eliminated ? (
                            <Chip
                              size="small"
                              label="Eliminated"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: "#e5e7eb",
                                color: "#6b7280",
                              }}
                            />
                          ) : null}
                        </Stack>
                        <Typography fontSize={12} color="text.secondary">
                          Public {row.public_votes} · Judge avg{" "}
                          {row.judge_score_count
                            ? Math.round(row.judge_score_sum / row.judge_score_count)
                            : "—"}{" "}
                          · Adj {row.admin_adjustment}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.25}>
                        <Chip
                          label={`${Number(row.public_votes) || 0} votes`}
                          sx={{
                            fontWeight: 700,
                            bgcolor: eliminated ? "#9ca3af" : "#1a1d27",
                            color: eliminated ? "#fff" : gold,
                          }}
                        />
                        {row.judge_score_count > 0 ? (
                          <Typography fontSize={11} color="text.secondary">
                            Judge avg{" "}
                            {Math.round(row.judge_score_sum / row.judge_score_count)}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                });
              })()}
            </Stack>
          </Paper>
        ) : null}

        <Dialog
          open={Boolean(confirmAction)}
          onClose={() => !actionLoading && setConfirmAction(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {confirmAction?.type === "enable"
              ? "Enable this round?"
              : confirmAction?.type === "stop"
                ? "Stop this round?"
                : confirmAction?.type === "advance"
                  ? "Complete round and advance?"
                  : "End Final Night?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmAction?.type === "enable" ? (
                <>
                  You are about to activate <strong>{confirmAction.round?.name}</strong>.
                  Judges and voters will score for this round. Only one round can be
                  active at a time.
                </>
              ) : null}
              {confirmAction?.type === "stop" ? (
                <>
                  You are about to stop <strong>{confirmAction.round?.name}</strong>.
                  Scoring and voting for this round will pause until you enable a round
                  again. Previous completed rounds cannot be re-enabled.
                </>
              ) : null}
              {confirmAction?.type === "advance" ? (
                <>
                  This will mark <strong>{confirmAction.round?.name}</strong> as
                  completed and start <strong>{confirmAction.nextRound?.name}</strong>.
                  Public vote counts reset for the new round. You will not be able to
                  re-enable {confirmAction.round?.name}.
                </>
              ) : null}
              {confirmAction?.type === "end" ? (
                <>
                  This will end Final Night for all users. Active round controls will
                  stop until you start Final Night again.
                </>
              ) : null}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmAction(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={confirmAction?.type === "end" ? "error" : "primary"}
              disabled={actionLoading}
              onClick={runConfirmedAction}
              sx={
                confirmAction?.type !== "end"
                  ? { bgcolor: gold, "&:hover": { bgcolor: "#b8925a" } }
                  : undefined
              }
            >
              {actionLoading ? "Please wait…" : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
